import React, { useEffect, useState } from 'react'
import { API_BASE_URL, LoadingSpinnerMeta } from '../data'
import type { Area, Event, MatchedVolunteer, PublicFormsMetaProps, Shift } from '../data/types'
import { getShiftDisplayName, sortByName, sortShifts } from '../utils/sorting'
import { LoadingSpinner } from './LoadingSpinner'
import { Button, Modal } from './ui'

type AllocationSelection = {
    eventId: number | null
    areaId: number | null
    shiftId: number | null
}

type Props = {
    meta: PublicFormsMetaProps
    isOpen: boolean
    onClose: () => void
    volunteers: MatchedVolunteer[]
    allocationSelection: AllocationSelection
    onAllocationSelectionChange: (selection: AllocationSelection) => void
    onSuccess: () => void
}

const parseErrorMessage = async (response: Response): Promise<string> => {
    const text = await response.text()
    try {
        const parsed = JSON.parse(text)
        if (parsed.errors) return Object.values(parsed.errors).flat().map(String).join(' ')
        return parsed.message || text
    } catch {
        return text
    }
}

const normalizeApiList = <T,>(result: any): T[] => {
    let data = result.value || result

    if (Array.isArray(data) && data.length > 0 && Array.isArray(data[0])) {
        data = data[0]
    }

    return Array.isArray(data) ? data : []
}

export const FormAllocateModal = (props: Props) => {
    const { meta, isOpen, onClose, volunteers, allocationSelection, onAllocationSelectionChange, onSuccess } = props

    const [events, setEvents] = useState<Event[]>([])
    const [loadingEvents, setLoadingEvents] = useState(false)
    const [selectedEventId, setSelectedEventId] = useState<number | null>(allocationSelection.eventId)

    const [areas, setAreas] = useState<Area[]>([])
    const [loadingAreas, setLoadingAreas] = useState(false)
    const [selectedAreaId, setSelectedAreaId] = useState<number | null>(allocationSelection.areaId)

    const [shifts, setShifts] = useState<Shift[]>([])
    const [loadingShifts, setLoadingShifts] = useState(false)
    const [selectedShiftId, setSelectedShiftId] = useState<number | null>(allocationSelection.shiftId)

    const [submitting, setSubmitting] = useState(false)
    const [success, setSuccess] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const selectedShift = shifts.find(shift => shift.id === selectedShiftId) || null

    useEffect(() => {
        const fetchEvents = async () => {
            setLoadingEvents(true)
            setError(null)
            try {
                const response = await fetch(`${API_BASE_URL}/event`, { credentials: 'include' })
                if (!response.ok) throw new Error(await parseErrorMessage(response))
                const data = await response.json()
                setEvents(sortByName(normalizeApiList<Event>(data)))
            } catch (err) {
                setError(err instanceof Error ? err.message : meta.errorTitle)
            } finally {
                setLoadingEvents(false)
            }
        }

        if (isOpen) {
            setSelectedEventId(allocationSelection.eventId)
            setSelectedAreaId(allocationSelection.areaId)
            setSelectedShiftId(allocationSelection.shiftId)
            setSuccess(false)
            setError(null)
            fetchEvents()
            return
        }

        setEvents([])
        setAreas([])
        setShifts([])
        setSuccess(false)
        setError(null)
    }, [isOpen, allocationSelection.eventId, allocationSelection.areaId, allocationSelection.shiftId, meta.errorTitle])

    useEffect(() => {
        if (!isOpen || selectedEventId === null) {
            setAreas([])
            return
        }

        const fetchAreas = async () => {
            setLoadingAreas(true)
            setError(null)
            try {
                const response = await fetch(`${API_BASE_URL}/area/${selectedEventId}`, { credentials: 'include' })
                if (!response.ok) throw new Error(await parseErrorMessage(response))
                const data = await response.json()
                setAreas(sortByName(normalizeApiList<Area>(data)))
            } catch (err) {
                setError(err instanceof Error ? err.message : meta.errorTitle)
            } finally {
                setLoadingAreas(false)
            }
        }

        fetchAreas()
    }, [isOpen, selectedEventId, meta.errorTitle])

    useEffect(() => {
        if (!isOpen || selectedAreaId === null) {
            setShifts([])
            return
        }

        const fetchShifts = async () => {
            setLoadingShifts(true)
            setError(null)
            try {
                const response = await fetch(`${API_BASE_URL}/shift/${selectedAreaId}`, { credentials: 'include' })
                if (!response.ok) throw new Error(await parseErrorMessage(response))
                const data = await response.json()
                setShifts(sortShifts(normalizeApiList<Shift>(data)))
            } catch (err) {
                setError(err instanceof Error ? err.message : meta.errorTitle)
            } finally {
                setLoadingShifts(false)
            }
        }

        fetchShifts()
    }, [isOpen, selectedAreaId, meta.errorTitle])

    const handleEventChange = async (eventId: number | null) => {
        setSelectedEventId(eventId)
        setAreas([])
        setSelectedAreaId(null)
        setShifts([])
        setSelectedShiftId(null)
        setError(null)
        onAllocationSelectionChange({ eventId, areaId: null, shiftId: null })
    }

    const handleAreaChange = async (areaId: number | null) => {
        setSelectedAreaId(areaId)
        setShifts([])
        setSelectedShiftId(null)
        setError(null)
        onAllocationSelectionChange({ eventId: selectedEventId, areaId, shiftId: null })
    }

    const handleShiftChange = (shiftId: number | null) => {
        setSelectedShiftId(shiftId)
        setError(null)
        onAllocationSelectionChange({ eventId: selectedEventId, areaId: selectedAreaId, shiftId })
    }

    const handleApproveAndAllocate = async () => {
        if (!selectedShift || volunteers.length === 0) return

        setSubmitting(true)
        setError(null)
        try {
            for (const volunteer of volunteers) {
                const response = await fetch(`${API_BASE_URL}/volunteer/${volunteer.csid}/approve`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify({
                        approved_status: true,
                        area_id: selectedAreaId,
                        shift_id: selectedShift.id
                    })
                })
                if (!response.ok) throw new Error(await parseErrorMessage(response))
            }
            setSuccess(true)
            onSuccess()
        } catch (err) {
            setError(err instanceof Error ? err.message : meta.errorTitle)
        } finally {
            setSubmitting(false)
        }
    }

    const renderShiftSelector = () => {
        if (loadingShifts) {
            return <LoadingSpinner meta={LoadingSpinnerMeta} />
        }

        if (shifts.length === 0) {
            return <p className="text-sm text-gray-500 dark:text-gray-400">{meta.allocateNoShiftsMessage}</p>
        }

        return (
            <select
                className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
                value={selectedShiftId ?? ''}
                onChange={event => handleShiftChange(event.target.value ? Number(event.target.value) : null)}
                disabled={selectedAreaId === null}
            >
                <option value="">{meta.allocateShiftLabel}</option>
                {shifts.map(shift => (
                    <option key={shift.id} value={shift.id}>
                        {getShiftDisplayName(shift)} ({shift.start_time.slice(0, 5)} - {shift.end_time.slice(0, 5)})
                        {shift.allocation_count !== undefined && shift.required_volunteer_count > 0 ? ` (${shift.allocation_count}/${shift.required_volunteer_count})` : ''}
                    </option>
                ))}
            </select>
        )
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={meta.allocateModalTitle}
            maxWidth="lg"
            footer={
                <div className="flex flex-row-reverse gap-3">
                    {!success && (
                        <Button onClick={handleApproveAndAllocate} disabled={!selectedShift || submitting}>
                            {submitting ? meta.approveAndAllocatingButton : meta.approveAndAllocateConfirmButton}
                        </Button>
                    )}
                    <Button variant="outline" color="neutral" onClick={onClose}>{meta.allocateCloseButton}</Button>
                </div>
            }
        >
            <div className="p-6 space-y-5">
                <div className="rounded-lg bg-gray-50 dark:bg-gray-900 px-4 py-3 text-sm flex flex-col sm:flex-row sm:items-center gap-1">
                    {volunteers.length === 1 ? (
                        <>
                            <span className="font-medium text-gray-900 dark:text-gray-100">{volunteers[0].first_name} {volunteers[0].last_name}</span>
                            <span className="text-gray-500 dark:text-gray-400 sm:ms-2">{volunteers[0].email}</span>
                        </>
                    ) : (
                        <span className="font-medium text-gray-900 dark:text-gray-100">{volunteers.length} identified volunteers</span>
                    )}
                </div>

                {error && (
                    <div className="bg-danger-100 dark:bg-danger-900 border border-danger-400 dark:border-danger-700 text-danger-700 dark:text-danger-200 px-4 py-3 rounded-lg text-sm">
                        {error}
                    </div>
                )}

                {success ? (
                    <div className="bg-success-100 dark:bg-success-900 border border-success-400 dark:border-success-700 text-success-700 dark:text-success-200 px-4 py-3 rounded-lg text-sm">
                        {meta.approveAndAllocateSuccessMessage}
                    </div>
                ) : (
                    <>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{meta.allocateEventLabel}</label>
                            {loadingEvents ? (
                                <LoadingSpinner meta={LoadingSpinnerMeta} />
                            ) : (
                                <select
                                    className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
                                    value={selectedEventId ?? ''}
                                    onChange={event => handleEventChange(event.target.value ? Number(event.target.value) : null)}
                                >
                                    <option value="">{meta.allocateSelectEventPlaceholder}</option>
                                    {events.map(event => (
                                        <option key={event.id} value={event.id}>{event.name}</option>
                                    ))}
                                </select>
                            )}
                        </div>

                        {selectedEventId !== null && !loadingEvents && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{meta.allocateAreaLabel}</label>
                                {loadingAreas ? (
                                    <LoadingSpinner meta={LoadingSpinnerMeta} />
                                ) : (
                                    <select
                                        className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
                                        value={selectedAreaId ?? ''}
                                        onChange={event => handleAreaChange(event.target.value ? Number(event.target.value) : null)}
                                        disabled={selectedEventId === null}
                                    >
                                        <option value="">{meta.allocateSelectAreaPlaceholder}</option>
                                        {areas.map(area => (
                                            <option key={area.id} value={area.id}>{area.name}</option>
                                        ))}
                                    </select>
                                )}
                            </div>
                        )}

                        {selectedAreaId !== null && !loadingAreas && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{meta.allocateShiftLabel}</label>
                                {renderShiftSelector()}
                            </div>
                        )}
                    </>
                )}
            </div>
        </Modal>
    )
}